import React from 'react';
import { 
    Box, 
    Typography, 
    Card, 
    CardContent, 
    Chip
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    AccessTime as AccessTimeIcon,
    CalendarToday as CalendarTodayIcon,
    Send as SendIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import RenderSocialIcons from '../RenderSocialIcons';

const StyledPostCard = styled(Card)(({ theme, isMobile, eventType }) => ({
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    border: `2px solid ${eventType === 'scheduled' ? theme.palette.info.light : theme.palette.primary.light}`,
    backgroundColor: eventType === 'scheduled' ? 
        `${theme.palette.info.main}08` : // Light blue tint for scheduled
        `${theme.palette.background.paper}`, // Normal background for immediate
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
        borderColor: eventType === 'scheduled' ? theme.palette.info.main : theme.palette.primary.main,
    },
    ...(isMobile ? {
        minWidth: 280,
        maxWidth: 300,
    } : {
        width: '100%',
        minHeight: 120,
    }),
}));

const PostHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
}));

const StatusChip = styled(Chip)(({ theme, status, eventType }) => ({
    fontSize: '0.75rem',
    height: 22,
    fontWeight: 600,
    '& .MuiChip-icon': {
        fontSize: '0.875rem',
    },
    // Status-based colors
    ...(status === 'posted' && {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
    }),
    ...(status === 'scheduled' && {
        backgroundColor: theme.palette.info.main,
        color: theme.palette.info.contrastText,
    }),
    ...(status === 'draft' && {
        backgroundColor: theme.palette.grey[400],
        color: theme.palette.common.white,
    }),
    ...(status === 'error' && {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
    }),
}));

const EventTypeIndicator = styled(Box)(({ theme, eventType }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.75),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: eventType === 'scheduled' ? 
        theme.palette.info.light : 
        theme.palette.primary.light,
    color: eventType === 'scheduled' ? 
        theme.palette.info.contrastText : 
        theme.palette.primary.contrastText,
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
}));

/**
 * PostCard Component
 * 
 * Displays a single post with status, content preview, media, and platform icons.
 * Now includes visual distinction between scheduled and immediate posts.
 * 
 * @param {Object} props
 * @param {Object} props.post - Post object containing all post data
 * @param {Function} props.onClick - Callback when post card is clicked
 * @param {boolean} props.isMobile - Whether to use mobile layout sizing
 * @param {number} props.maxTextLength - Maximum characters to show in preview
 */
const PostCard = ({ post, onClick, isMobile = false, maxTextLength = 120 }) => {
    
    // Get post status - use new backend field if available, otherwise calculate
    const getPostStatus = () => {
        // Use the new status field from backend if available
        if (post.status) {
            return post.status;
        }
        
        // Legacy fallback logic
        const hasError = post.platform_details?.some(detail => detail.error || detail.failed);
        const isScheduled = post.is_scheduled_post || dayjs(post.scheduled_post_date).isAfter(dayjs());
        const isPosted = post.platform_details?.every(detail => detail.is_posted) || post.is_posted;

        if (hasError) return 'error';
        if (isPosted) return 'posted';
        if (isScheduled) return 'scheduled';
        return 'draft';
    };

    // Get event type - use new backend field if available
    const getEventType = () => {
        return post.event_type || (post.is_scheduled_post ? 'scheduled' : 'immediate');
    };

    // Get status icon
    const getStatusIcon = (status) => {
        switch (status) {
            case 'posted':
                return <CheckCircleIcon />;
            case 'scheduled':
                return <AccessTimeIcon />;
            case 'error':
                return <ErrorIcon />;
            default:
                return <ScheduleIcon />;
        }
    };

    // Get status label
    const getStatusLabel = (status) => {
        switch (status) {
            case 'posted':
                return 'Posted';
            case 'scheduled':
                return 'Scheduled';
            case 'error':
                return 'Error';
            default:
                return 'Draft';
        }
    };

    // Get event type icon
    const getEventTypeIcon = (eventType) => {
        return eventType === 'scheduled' ? <CalendarTodayIcon /> : <SendIcon />;
    };

    // Get event type label
    const getEventTypeLabel = (eventType) => {
        return eventType === 'scheduled' ? 'Scheduled Post' : 'Immediate Post';
    };

    // Format post time - use display_date if available
    const formatPostTime = () => {
        const date = post.display_date || post.scheduled_post_date || post.created_at;
        return dayjs(date).format('h:mm A');
    };

    // Format post date for display
    const formatPostDate = () => {
        const date = post.display_date || post.scheduled_post_date || post.created_at;
        const postDay = dayjs(date);
        const today = dayjs();
        
        if (postDay.isSame(today, 'day')) {
            return 'Today';
        } else if (postDay.isSame(today.add(1, 'day'), 'day')) {
            return 'Tomorrow';
        } else if (postDay.isSame(today.subtract(1, 'day'), 'day')) {
            return 'Yesterday';
        }
        return postDay.format('MMM D');
    };

    // Truncate text
    const truncateText = (text, maxLength) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const status = getPostStatus();
    const eventType = getEventType();

    return (
        <StyledPostCard
            elevation={1}
            onClick={onClick}
            isMobile={isMobile}
            eventType={eventType}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <PostHeader>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                        <Typography 
                            variant="caption" 
                            color="text.secondary"
                            fontWeight="medium"
                        >
                            {formatPostDate()} at {formatPostTime()}
                        </Typography>
                        <EventTypeIndicator eventType={eventType}>
                            {getEventTypeIcon(eventType)}
                            <Typography variant="caption" fontWeight="inherit">
                                {getEventTypeLabel(eventType)}
                            </Typography>
                        </EventTypeIndicator>
                    </Box>
                    <StatusChip
                        icon={getStatusIcon(status)}
                        label={getStatusLabel(status)}
                        size="small"
                        status={status}
                        eventType={eventType}
                    />
                </PostHeader>
                
                <Typography 
                    variant="body2" 
                    sx={{ 
                        mb: 1.5,
                        lineHeight: 1.4,
                        color: 'text.primary'
                    }}
                >
                    {truncateText(post.content || post.post_text, maxTextLength)}
                </Typography>
                
                {post.img_url && (
                    <Box sx={{ mb: 1.5 }}>
                        <img
                            src={post.img_url}
                            alt={post.img_alt_text || 'Post media'}
                            style={{
                                width: '100%',
                                height: 80,
                                objectFit: 'cover',
                                borderRadius: 4,
                            }}
                        />
                    </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RenderSocialIcons post={post} />
                        {post.is_thread && post.thread_order === 1 && (
                            <Chip
                                label={`Thread${post.thread_posts ? ` (${post.thread_posts.length + 1} posts)` : ''}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        ID: {post.id}
                    </Typography>
                </Box>
                
                {/* Display thread posts if this is a parent thread */}
                {post.is_thread && post.thread_order === 1 && post.thread_posts && post.thread_posts.length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Thread Posts:
                        </Typography>
                        {post.thread_posts.map((threadPost, index) => (
                            <Box 
                                key={threadPost.id} 
                                sx={{ 
                                    mb: 1, 
                                    pl: 2, 
                                    borderLeft: 2, 
                                    borderColor: 'primary.light',
                                    '&:last-child': { mb: 0 }
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    {index + 2}. ID: {threadPost.id}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {truncateText(threadPost.content || threadPost.post_text, 100)}
                                </Typography>
                                {threadPost.img_url && (
                                    <Box sx={{ mt: 0.5 }}>
                                        <img
                                            src={threadPost.img_url}
                                            alt={threadPost.img_alt_text || 'Thread media'}
                                            style={{
                                                height: 40,
                                                width: 60,
                                                objectFit: 'cover',
                                                borderRadius: 4,
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </CardContent>
        </StyledPostCard>
    );
};

export default PostCard; 